import { Button, Tag } from "antd";
import {
  AppWindow,
  Calculator,
  LayoutDashboard,
  Plus,
  ShieldCheck,
  ShoppingBag,
} from "lucide-react";
import { useEffect, useState } from "react";

import { getEmployeeRoles } from "@/api/system/roles";
import Modal from "@/components/Modal";
import { usePermission } from "@/hooks/usePermission";
import { cn } from "@/lib/cn";

import { RolePermissionLoading, RolePermissionView } from "./RolePermissionView";
import type { RoleDetailModalProps } from "./types";
import { useRoleDetail } from "./useRoleDetail";
import { normalizeRoleName } from "./utils";

export type { RoleDetailModalProps, RoleDetailSource, RolePermOpenParams } from "./types";

/** 系统端编码 → 图标（基本信息区 Tag 用） */
const SYSTEM_TYPE_ICON: Record<string, React.ReactNode> = {
  BIZ: <ShoppingBag className="w-3 h-3" />,
  POS: <Calculator className="w-3 h-3" />,
  WKS: <LayoutDashboard className="w-3 h-3" />,
  OPS: <AppWindow className="w-3 h-3" />,
};

/**
 * 从员工已绑角色列表提取去重后的角色名（展示用）
 * @param roles 员工角色列表
 */
const collectUniqueRoleNames = (roles: { roleName?: string }[]): string[] => {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const item of roles) {
    const raw = item.roleName?.trim();
    if (!raw) continue;
    const key = normalizeRoleName(raw);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    names.push(raw.replace(/模板$/, "").trim() || raw);
  }
  return names;
};

/**
 * 角色权限详情弹窗：支持员工 / 账号 / 角色 ID 三种数据来源
 * 员工场景展示多角色 Tab +「添加角色」
 */
export const RoleDetailModal = ({
  open,
  onClose,
  roleName,
  source,
  employeeDisplayName,
  onAddRole,
  refreshKey = 0,
}: RoleDetailModalProps) => {
  const canAssignRole = usePermission("ops:employee:role:assign");
  /** 员工多角色 Tab */
  const [roleTabs, setRoleTabs] = useState<string[]>([]);
  /** 当前选中的角色名 */
  const [activeRoleName, setActiveRoleName] = useState(roleName);
  /** Tab 列表加载中 */
  const [tabsLoading, setTabsLoading] = useState(false);

  // 员工场景：拉取已绑角色作为 Tab；其它场景沿用传入 roleName
  useEffect(() => {
    if (!open) {
      setRoleTabs([]);
      setActiveRoleName(roleName);
      setTabsLoading(false);
      return;
    }
    if (source?.mode !== "employee") {
      setRoleTabs([]);
      setActiveRoleName(roleName);
      setTabsLoading(false);
      return;
    }

    let cancelled = false;
    setTabsLoading(true);
    void getEmployeeRoles({ employeeId: source.employeeId, orgId: source.orgId })
      .then((res) => {
        if (cancelled) return;
        const names = collectUniqueRoleNames(res.data ?? []);
        setRoleTabs(names);
        const preferred = normalizeRoleName(roleName);
        const matched = names.find((name) => normalizeRoleName(name) === preferred);
        setActiveRoleName(matched ?? names[0] ?? roleName);
      })
      .catch(() => {
        if (cancelled) return;
        setRoleTabs(roleName ? [roleName] : []);
        setActiveRoleName(roleName);
      })
      .finally(() => {
        if (!cancelled) setTabsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, roleName, source, refreshKey]);

  const { data, loading } = useRoleDetail(open, activeRoleName, source);
  const showEmployeeTabs = source?.mode === "employee";
  const displayTitle =
    (showEmployeeTabs && employeeDisplayName?.trim()) ||
    data?.roleName ||
    activeRoleName.replace(/模板$/, "").trim() ||
    activeRoleName ||
    roleName;
  const showAddRole = showEmployeeTabs && canAssignRole && !!onAddRole;

  return (
    <Modal
      open={open}
      width={900}
      title={displayTitle}
      subtitle="角色权限详情"
      titleIcon={<ShieldCheck className="w-5 h-5 text-orange-500" />}
      footer={
        <div className="flex justify-end w-full">
          <Button onClick={onClose}>关闭</Button>
        </div>
      }
      onCancel={onClose}
    >
      {showEmployeeTabs ? (
        <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-1 flex-wrap min-w-0">
            {tabsLoading ? (
              <span className="text-xs text-slate-400">角色加载中…</span>
            ) : roleTabs.length > 0 ? (
              roleTabs.map((name) => {
                const active = normalizeRoleName(name) === normalizeRoleName(activeRoleName);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setActiveRoleName(name)}
                    className={cn(
                      "px-2.5 py-1 rounded text-xs font-medium transition-colors",
                      active
                        ? "bg-orange-500 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                    )}
                  >
                    {name}
                  </button>
                );
              })
            ) : (
              <span className="text-xs text-slate-400">暂无已分配角色</span>
            )}
          </div>
          {showAddRole ? (
            <button
              type="button"
              onClick={onAddRole}
              className="inline-flex items-center gap-1 shrink-0 px-2.5 py-1 rounded text-xs font-medium bg-orange-500 text-white hover:bg-orange-600"
            >
              <Plus className="w-3 h-3" />
              添加角色
            </button>
          ) : null}
        </div>
      ) : null}

      {!source ? (
        <div className="text-center py-8 text-slate-500 text-sm">
          <ShieldCheck className="h-10 w-10 mx-auto mb-2 opacity-30" />
          <p>缺少角色查询来源，无法加载权限</p>
        </div>
      ) : loading || tabsLoading ? (
        <RolePermissionLoading />
      ) : !data ? (
        <div className="text-center py-8 text-slate-500 text-sm">
          <ShieldCheck className="h-10 w-10 mx-auto mb-2 opacity-30" />
          <p>暂无该角色的权限配置信息</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-[65vh] overflow-y-auto">
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-500 shrink-0">角色名称</span>
              <span className="font-medium">{data.roleName || activeRoleName}</span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-500 shrink-0">所属端</span>
              <div className="flex flex-wrap gap-1.5">
                {data.terminals.length > 0 ? (
                  data.terminals.map((terminal) => (
                    <Tag
                      key={terminal.systemTypeId}
                      bordered
                      className="inline-flex! items-center gap-1 text-xs m-0 py-0.5 leading-none"
                    >
                      <span className="inline-flex shrink-0">
                        {SYSTEM_TYPE_ICON[terminal.systemTypeCode]}
                      </span>
                      {terminal.systemTypeCode}
                    </Tag>
                  ))
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </div>
            </div>
            <div className="flex items-start gap-4 text-sm">
              <span className="text-slate-500 shrink-0">角色描述</span>
              <span className="text-slate-800">{data.roleDesc || "—"}</span>
            </div>
          </div>
          <RolePermissionView data={data} />
        </div>
      )}
    </Modal>
  );
};
